import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Phone, Mail, Pencil, Trash2, PowerOff, Power, Search } from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePagination } from "@/components/Pagination";

const EMPTY = {
  name: "", phone: "", email: "", pan: "", aadhaar: "", gst: "", address: "",
  unit_id: "", total_amount: 0, paid_amount: 0,
  booking_date: "", agreement_date: "", registration_date: "",
  nominee_name: "", nominee_relation: "", nominee_phone: "",
  loan_bank: "", loan_amount: 0, loan_sanction_date: "",
  payment_schedule: "", notes: "", status: "booked", active: true,
};

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");

  const { data: customers = [] } = useQuery({
    queryKey: ["customers", projectId],
    queryFn: async () => (await api.get("/customers", { params: projectId ? { project_id: projectId } : {} })).data,
  });

  const filtered = customers.filter((c) => {
    if (!showInactive && c.active === false) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.pan || "").toLowerCase().includes(q)
    );
  });

  const { pageRows, controls } = usePagination(filtered);

  const { data: units = [] } = useQuery({
    queryKey: ["units-all", projectId, editingId],
    queryFn: async () => (await api.get("/units", { params: { project_id: projectId } })).data,
    enabled: !!projectId,
  });
  const availableUnits = units.filter((u) => ["available", "reserved"].includes(u.status) || u.id === form.unit_id);

  function openNew() {
    if (!projectId) return toast.error("Select a project filter first");
    setForm({ ...EMPTY, project_id: projectId });
    setEditingId(null);
    setOpen(true);
  }
  function openEdit(c) {
    setForm({ ...EMPTY, ...c });
    setEditingId(c.id);
    if (c.project_id) setProjectId(c.project_id);
    setOpen(true);
  }

  function invalidate() {
    ["customers", "units", "units-all", "units-avail", "dashboard-kpi"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  }

  async function save() {
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    if (!editingId && !projectId) return toast.error("Select a project");
    try {
      // Convert empty strings to null so backend Pydantic Optional[...] fields pass validation
      const clean = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" && v.trim() === "" ? null : v]));
      const payload = {
        ...clean,
        total_amount: Number(form.total_amount) || 0,
        paid_amount: Number(form.paid_amount) || 0,
        loan_amount: Number(form.loan_amount) || 0,
        unit_id: form.unit_id || null,
      };
      if (editingId) {
        payload._reason = window.prompt("Reason for edit (audit)?", "") || "Edited";
        await api.patch(`/customers/${editingId}`, payload);
        toast.success("Customer updated");
      } else {
        payload.project_id = projectId;
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      invalidate();
      setOpen(false); setForm(EMPTY); setEditingId(null);
    } catch (e) { toast.error(apiErr(e, "Failed to save customer")); }
  }

  async function toggleActive(c) {
    try {
      await api.patch(`/customers/${c.id}`, { active: !(c.active !== false), _reason: c.active === false ? "Reactivated" : "Marked inactive" });
      toast.success(c.active === false ? "Customer reactivated" : "Customer marked inactive");
      invalidate();
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function del(c) {
    if (!window.confirm(`Permanently delete customer ${c.name}?\n\nNote: If they have bookings/payments/agreements, delete will be blocked. Use "Mark Inactive" instead.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      invalidate();
    } catch (e) {
      const detail = apiErr(e);
      if (isAdmin && detail.includes("bookings/payments/agreements")) {
        if (window.confirm(`${detail}\n\nAs Admin, do you want to FORCE DELETE anyway? This will free the unit.`)) {
          try { await api.delete(`/customers/${c.id}`, { params: { force: true } }); toast.success("Force-deleted"); invalidate(); }
          catch (er) { toast.error(apiErr(er)); }
        }
      } else { toast.error(detail); }
    }
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">CRM</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Customers</h1>
          <p className="text-sm text-zinc-500 mt-1">360° view of buyers — bookings, payments, agreements, possession.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Search name, phone, email, PAN…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-64 pl-8"
              data-testid="customer-search"
            />
          </div>
          <label className="flex items-center gap-1 text-xs text-zinc-500 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="show-inactive" /> Show inactive
          </label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="customer-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-customer-btn"><Plus size={14} className="mr-1" /> Add Customer</Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditingId(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {!editingId && (
              <div className="col-span-3"><Label>Project</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger data-testid="new-cust-project"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
            <div><Label>Phone *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
            <div><Label>Aadhaar</Label><Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
            <div className="col-span-3"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>

            <div className="col-span-3 mt-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500 border-b pb-1">Booking & Agreement</div></div>
            <div className="col-span-3"><Label>Assign Unit <span className="text-[10px] text-emerald-700 font-normal ml-2">Link a unit to see this customer on the Sales Board immediately</span></Label>
              <Select value={form.unit_id || " "} onValueChange={(v) => setForm({ ...form, unit_id: v.trim() })}>
                <SelectTrigger data-testid="cust-unit"><SelectValue placeholder="No unit" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">— No unit —</SelectItem>
                  {availableUnits.map((u) => (<SelectItem key={u.id} value={u.id}>{u.unit_number} · {inr(u.price)} · {u.status}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Booking Date</Label><Input type="date" value={(form.booking_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, booking_date: e.target.value })} /></div>
            <div><Label>Agreement Date</Label><Input type="date" value={(form.agreement_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, agreement_date: e.target.value })} /></div>
            <div><Label>Registration Date</Label><Input type="date" value={(form.registration_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, registration_date: e.target.value })} /></div>
            <div><Label>Total Amount (₹)</Label><Input type="number" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="cust-total" /></div>
            <div><Label>Paid Amount (₹)</Label><Input type="number" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="agreement_done">Agreement Done</SelectItem>
                  <SelectItem value="registered">Registered</SelectItem>
                  <SelectItem value="possession">Possession</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-3 mt-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500 border-b pb-1">Nominee</div></div>
            <div><Label>Nominee Name</Label><Input value={form.nominee_name} onChange={(e) => setForm({ ...form, nominee_name: e.target.value })} /></div>
            <div><Label>Relation</Label><Input value={form.nominee_relation} onChange={(e) => setForm({ ...form, nominee_relation: e.target.value })} /></div>
            <div><Label>Nominee Phone</Label><Input value={form.nominee_phone} onChange={(e) => setForm({ ...form, nominee_phone: e.target.value })} /></div>

            <div className="col-span-3 mt-2"><div className="text-[10px] uppercase tracking-widest text-zinc-500 border-b pb-1">Home Loan</div></div>
            <div><Label>Bank</Label><Input value={form.loan_bank} onChange={(e) => setForm({ ...form, loan_bank: e.target.value })} /></div>
            <div><Label>Loan Amount (₹)</Label><Input type="number" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} /></div>
            <div><Label>Sanction Date</Label><Input type="date" value={(form.loan_sanction_date || "").slice(0, 10)} onChange={(e) => setForm({ ...form, loan_sanction_date: e.target.value })} /></div>

            <div className="col-span-3"><Label>Payment Schedule / Plan</Label><Input value={form.payment_schedule} onChange={(e) => setForm({ ...form, payment_schedule: e.target.value })} placeholder="e.g., 20% on booking, 30% on plinth, 30% on slab, 20% on possession" /></div>
            <div className="col-span-3"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-customer">{editingId ? "Save changes" : "Save customer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="bg-white border-zinc-200 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest">Customer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Contact</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Booking</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-right">Paid</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-right">Outstanding</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((c) => {
                const inactive = c.active === false;
                return (
                  <TableRow key={c.id} className={`data-row ${inactive ? "opacity-50" : ""}`} data-testid={`customer-row-${c.id}`}>
                    <TableCell>
                      <div className="font-medium">{c.name}{inactive && <span className="ml-2 text-[10px] bg-zinc-200 text-zinc-700 px-1.5 rounded">INACTIVE</span>}</div>
                      <div className="text-[11px] text-zinc-500">PAN {c.pan || "—"}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.phone && <div className="flex items-center gap-1"><Phone size={11} />{c.phone}</div>}
                      {c.email && <div className="flex items-center gap-1"><Mail size={11} />{c.email}</div>}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{fmtDate(c.booking_date)}</TableCell>
                    <TableCell className="text-right font-mono">{inr(c.total_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{inr(c.paid_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{inr(c.outstanding)}</TableCell>
                    <TableCell><span className="text-[11px] uppercase tracking-widest bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded">{c.status}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil size={12} /></Button>
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => toggleActive(c)} data-testid={`toggle-active-${c.id}`}>
                        {inactive ? <Power size={12} /> : <PowerOff size={12} />}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => del(c)} data-testid={`delete-customer-${c.id}`}><Trash2 size={12} /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!filtered.length && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-10">No customers yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {controls}
        </CardContent>
      </Card>
    </div>
  );
}
